/* eslint-disable react/destructuring-assignment */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { space, typography, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Select } from 'antd';
import InputError from 'theme/input-error';

const { Option } = Select;

const StyledSelect = styled.div`
  ${space}
  ${typography}
  ${layout}

  width: 100%;

  .ant-select {
    width: 100%;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    color: ${themeGet('colors.grey_400')};
    font-size: 16px;
    font-variant: tabular-nums;
    line-height: 1.5715;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    display: inline-block;
  }

  .ant-select-selector {
    display: flex;
    position: relative;
    background-color: ${themeGet('colors.white')};
    border: 2px solid;
    border-color: ${({ error }) =>
      error ? themeGet('colors.red_400') : themeGet('colors.primary_100')};
    border-radius: 4px;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
    height: 48px;
    padding: 0 24px 0 12px;
    outline: 0;

    :hover {
      border-color: ${themeGet('colors.primary_300')};
    }

    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    border-color: ${themeGet('colors.primary_500')};
    box-shadow: none;
  }

  .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${({ error }) =>
      error ? themeGet('colors.red_400') : themeGet('colors.primary_200')};
  }

  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    width: 100%;
    height: 48px;
    border: 2px solid;
    border-color: ${({ error }) =>
      error ? themeGet('colors.red_400') : themeGet('colors.primary_100')};
    border-radius: 4px;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  .ant-select-open .ant-select-selector {
    border-color: ${themeGet('colors.primary_500')};
  }

  .ant-select-single .ant-select-selector .ant-select-selection-search {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 11px;
    right: 25px;
  }

  .ant-select-selection-placeholder {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    opacity: 0.4;
  }

  .ant-select-selection-item {
    display: flex;
    align-items: center;
    padding: 0;
    line-height: 30px;
    transition: all 0.3s;
    pointer-events: none;
  }

  .ant-select-selection-search-input {
    cursor: pointer;
    width: 100%;
    height: 100% !important;
    background: transparent;
    border: none;
    outline: none;
  }

  .ant-select:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search-input {
    padding: 0 6px;
  }

  .ant-select-arrow {
    display: inline-block;
    font-style: normal;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: absolute;
    top: 53%;
    right: 11px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
    color: ${themeGet('colors.primary_300')};
    font-size: 12px;
    line-height: 1;
    text-align: center;
    pointer-events: none;
  }

  .anticon {
    display: inline-block;
    color: inherit;
    font-style: normal;
    line-height: 0;
    text-align: center;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .ant-select-arrow > * {
    line-height: 1;
  }

  .ant-select-arrow .anticon {
    vertical-align: top;
    transition: -webkit-transform 0.3s;
    transition: transform 0.3s;
  }
`;

function CustomSelect({
  className,
  defaultValue,
  disabled,
  input,
  extraOnChange,
  meta,
  id,
  placeholder,
  options,
  style,
  noError,
  languageCode,
}) {
  const handleChange = useCallback(
    (e) => {
      input.onChange(e);

      if (extraOnChange) {
        extraOnChange(e);
      }
    },
    [input, extraOnChange],
  );

  return (
    <StyledSelect
      className={className}
      fontSize="display"
      lineHeight="display"
      fontWeight="normal"
      error={meta.error && meta.touched}
      disabled={disabled}
      {...style}
    >
      <Select
        id={id}
        placeholder={placeholder}
        {...input}
        onChange={handleChange}
        disabled={disabled}
        showSearch
        optionFilterProp="children"
        filterOption={(text, option) =>
          option.children.toLowerCase().indexOf(text.toLowerCase()) >= 0
        }
        defaultValue={defaultValue}
      >
        {options &&
          options.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
      </Select>

      {!noError && (
        <InputError value={input.value} meta={meta} languageCode={languageCode || 'en'} />
      )}
    </StyledSelect>
  );
}

export default CustomSelect;
export { StyledSelect };
